import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { ActionSurface } from '@/components/patterns/action-surface'
import { Can } from '@/lib/casl'
import { cn } from '@/lib/utils'
import {
  PRICE_ADJUSTMENT_PRIORITY_NORMAL,
  PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED,
} from '@/lib/services/price-adjustments/priceAdjustments.schema'
import {
  getPriceAdjustmentActionLabel,
  getPriceAdjustmentCardTitle,
  getPriceAdjustmentSignedAmountLabel,
  getPriceAdjustmentStateMeta,
  getPriceAdjustmentStatusMeta,
  getPriceAdjustmentSupportingTitle,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { ArrowDown, ArrowUp } from 'lucide-react'
import {
  showPageCardClass,
  showPageDecreaseIconShellClass,
  showPageIconShellClass,
  showPageIncreaseIconShellClass,
} from './constants'
import { getWhenTypeSummary } from './helpers'

export function PriceAdjustmentSummaryCard({ controller, placement = 'main' }) {
  const {
    disableMutation,
    enableMutation,
    form,
    initialData,
    initialDataCanEnable,
    persistedAdjustment,
    scheduleSectionId,
    scopeAndChangeSectionId,
    scrollToSection,
    setDisableDialogOpen,
    showPageTreatment,
    handleEnable,
  } = controller

  if (!initialData) {
    return null
  }

  const isSidePlacement = placement === 'side'
  const initialDataIsDecrease = persistedAdjustment?.operation === 'decrease'
  const InitialDataDirectionIcon = initialDataIsDecrease ? ArrowDown : ArrowUp
  const initialDataIsPriority =
    Number(persistedAdjustment?.priority ?? PRICE_ADJUSTMENT_PRIORITY_NORMAL) > 0
  const initialDataStatusMeta = getPriceAdjustmentStatusMeta(persistedAdjustment?.status)
  const initialDataStateMeta = getPriceAdjustmentStateMeta(persistedAdjustment)
  const ruleStateActionButton = initialDataCanEnable ? (
    <Button
      type="button"
      size="sm"
      variant="success"
      disabled={enableMutation.isPending}
      onClick={() => void handleEnable()}
    >
      Enable
    </Button>
  ) : (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={disableMutation.isPending}
      onClick={() => setDisableDialogOpen(true)}
    >
      Disable
    </Button>
  )

  return (
    <Card
      accent={showPageTreatment ? (initialDataIsDecrease ? 'amethyst' : 'mint') : 'none'}
      className={
        showPageTreatment
          ? cn(showPageCardClass, isSidePlacement && '@container/price-adjustment-summary')
          : undefined
      }
    >
      <CardContent
        className={cn(
          showPageTreatment ? 'space-y-4 p-5' : 'flex flex-wrap items-start justify-between gap-4',
          isSidePlacement && 'space-y-4'
        )}
      >
        {showPageTreatment ? (
          <>
            <div
              className={cn(
                'flex flex-col gap-3',
                isSidePlacement
                  ? '@lg/price-adjustment-summary:flex-row @lg/price-adjustment-summary:items-start @lg/price-adjustment-summary:justify-between'
                  : 'sm:flex-row sm:items-start sm:justify-between'
              )}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={cn(
                    showPageIconShellClass,
                    initialDataIsDecrease
                      ? showPageDecreaseIconShellClass
                      : showPageIncreaseIconShellClass
                  )}
                >
                  <InitialDataDirectionIcon className="size-5" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <PriceAdjustmentSummaryTitle
                    persistedAdjustment={persistedAdjustment}
                    initialDataStatusMeta={initialDataStatusMeta}
                    initialDataStateMeta={initialDataStateMeta}
                    initialDataIsPriority={initialDataIsPriority}
                    showPageTreatment
                  />
                </div>
              </div>
              <Can do="edit" on="products">
                <div
                  className={cn(
                    'shrink-0 self-start',
                    isSidePlacement && '@lg/price-adjustment-summary:self-auto'
                  )}
                >
                  {ruleStateActionButton}
                </div>
              </Can>
            </div>

            <div
              className={cn(
                'grid gap-3',
                isSidePlacement ? '@md/price-adjustment-summary:grid-cols-2' : 'sm:grid-cols-2'
              )}
            >
              <ActionSurface
                padding="compact"
                className="w-full flex-col gap-2"
                onClick={() => scrollToSection(scopeAndChangeSectionId)}
                aria-label="Edit price change"
              >
                <Typography variant="eyebrow">Price Change</Typography>
                <Typography
                  variant="body"
                  tone={initialDataIsDecrease ? 'interaction' : 'success'}
                  weight="semibold"
                  className="flex items-center gap-1.5"
                >
                  <InitialDataDirectionIcon className="size-4" aria-hidden="true" />
                  {getPriceAdjustmentSignedAmountLabel(persistedAdjustment)}
                </Typography>
              </ActionSurface>
              <form.Subscribe selector={(state) => state.values.when_type}>
                {(whenType) => (
                  <ActionSurface
                    padding="compact"
                    className="w-full flex-col gap-2"
                    onClick={() =>
                      scrollToSection(
                        whenType === PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED
                          ? scheduleSectionId
                          : scopeAndChangeSectionId
                      )
                    }
                    aria-label="Edit when this price change happens"
                  >
                    <Typography variant="eyebrow">When</Typography>
                    <Typography variant="supporting" wrap="pretty" className="block">
                      {getWhenTypeSummary(persistedAdjustment)}
                    </Typography>
                  </ActionSurface>
                )}
              </form.Subscribe>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <PriceAdjustmentSummaryTitle
                persistedAdjustment={persistedAdjustment}
                initialDataStatusMeta={initialDataStatusMeta}
                initialDataStateMeta={initialDataStateMeta}
                initialDataIsPriority={initialDataIsPriority}
              />
              <Typography variant="supporting" className="block">
                {getPriceAdjustmentActionLabel(persistedAdjustment)}
              </Typography>
              <Typography variant="supporting" className="block">
                {getWhenTypeSummary(persistedAdjustment)}
              </Typography>
            </div>
            <Can do="edit" on="products">
              <div className="shrink-0">{ruleStateActionButton}</div>
            </Can>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function PriceAdjustmentSummaryTitle({
  persistedAdjustment,
  initialDataStatusMeta,
  initialDataStateMeta,
  initialDataIsPriority,
  showPageTreatment = false,
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Typography
          variant="h4"
          wrap={showPageTreatment ? 'balance' : 'inherit'}
          className={showPageTreatment ? 'pb-0' : undefined}
        >
          {getPriceAdjustmentCardTitle(persistedAdjustment)}
        </Typography>
        <Badge variant={initialDataStatusMeta.variant} size="md">
          {initialDataStatusMeta.label}
        </Badge>
        {initialDataStateMeta && initialDataStateMeta.label !== initialDataStatusMeta.label ? (
          <Badge variant={initialDataStateMeta.variant} size="md">
            {initialDataStateMeta.label}
          </Badge>
        ) : null}
        {initialDataIsPriority ? (
          <Badge variant="info" size="md">
            Priority
          </Badge>
        ) : null}
      </div>
      {getPriceAdjustmentSupportingTitle(persistedAdjustment) ? (
        <Typography variant="label" tone="muted" className="block">
          {getPriceAdjustmentSupportingTitle(persistedAdjustment)}
        </Typography>
      ) : null}
    </>
  )
}
